/**
 * RoomEnd (PPL-35 → redesigned PPL-69 / C9) — graceful exit from a room.
 *
 * Per the C9 canvas: the screen asks what trace to keep, not whether to leave.
 * Three radio options, each equal-weight. A live "what the other side will
 * see" preview updates with the choice. No "are you sure" prompt.
 *
 * Three options:
 *  - keep summary  → endRoomKeepSummary → /rooms/:id/summary
 *  - just pause    → pauseRoom         → navigate(-1)
 *  - keep nothing  → deleteRoomForBoth → /rooms
 *
 * Privacy invariant: the other side is told quietly the next morning, never
 * with a push at 11pm. Copy in the preview makes that explicit.
 */

import React, { CSSProperties, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import {
  endRoomKeepSummary,
  pauseRoom,
  deleteRoomForBoth,
  subscribeToRoom,
  getMediationCounterpart,
} from '../services/roomService';
import type { Room } from '../types/room';
import SEO from '../components/SEO';
import RoomBlockHatch from '../components/RoomBlockHatch';

// ─── Atoms ───────────────────────────────────────────────────

function DualAurora() {
  return (
    <div
      aria-hidden="true"
      style={{
        position: 'absolute',
        inset: 0,
        overflow: 'hidden',
        zIndex: 0,
        pointerEvents: 'none',
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: '-22%',
          left: '-12%',
          width: '70%',
          height: '70%',
          borderRadius: '50%',
          background: 'radial-gradient(circle, #fb923c 0%, transparent 65%)',
          filter: 'blur(85px)',
          opacity: 0.32,
        }}
      />
      <div
        style={{
          position: 'absolute',
          bottom: '-30%',
          right: '-12%',
          width: '68%',
          height: '72%',
          borderRadius: '50%',
          background: 'radial-gradient(circle, #6366f1 0%, transparent 65%)',
          filter: 'blur(95px)',
          opacity: 0.36,
        }}
      />
    </div>
  );
}

function InlineRailCard({
  eyebrow,
  children,
}: {
  eyebrow: string;
  children: React.ReactNode;
}) {
  return (
    <div
      style={{
        position: 'relative',
        maxWidth: 720,
        margin: '24px auto 0',
        paddingLeft: 22,
      }}
    >
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          left: 0,
          top: 6,
          bottom: 4,
          width: 3,
          borderRadius: 3,
          background: 'linear-gradient(180deg, #d946ef, #a855f7)',
        }}
      />
      <div
        style={{
          fontSize: 10,
          fontWeight: 800,
          letterSpacing: '0.16em',
          textTransform: 'uppercase',
          color: '#f0abfc',
          marginBottom: 14,
        }}
      >
        {eyebrow}
      </div>
      {children}
    </div>
  );
}

function ExitOption({
  on,
  onClick,
  head,
  sub,
  disabled,
}: {
  on: boolean;
  onClick: () => void;
  head: string;
  sub: string;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-pressed={on}
      style={{
        appearance: 'none',
        cursor: disabled ? 'not-allowed' : 'pointer',
        width: '100%',
        padding: '14px 16px',
        borderRadius: 14,
        background: on ? 'rgba(255,255,255,0.06)' : 'rgba(255,255,255,0.03)',
        border: on
          ? '1px solid rgba(255,255,255,0.18)'
          : '1px solid rgba(255,255,255,0.08)',
        textAlign: 'left',
        fontFamily: 'inherit',
        display: 'flex',
        alignItems: 'flex-start',
        gap: 12,
        opacity: disabled && !on ? 0.55 : 1,
      }}
    >
      <div
        aria-hidden="true"
        style={{
          width: 18,
          height: 18,
          borderRadius: '50%',
          border: on ? '5px solid #fff' : '1.5px solid rgba(255,255,255,0.25)',
          background: 'transparent',
          marginTop: 3,
          flexShrink: 0,
        }}
      />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 14.5,
            fontWeight: 600,
            color: '#fff',
            letterSpacing: '-0.005em',
          }}
        >
          {head}
        </div>
        <div
          style={{
            fontSize: 12.5,
            color: 'rgba(232,220,250,0.62)',
            marginTop: 3,
            lineHeight: 1.5,
          }}
        >
          {sub}
        </div>
      </div>
    </button>
  );
}

function Avatar({ name, hue, size = 26 }: { name: string; hue: number; size?: number }) {
  const initials = name
    .split(' ')
    .map((w) => w[0])
    .filter(Boolean)
    .join('')
    .slice(0, 2)
    .toUpperCase();
  return (
    <span
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: `radial-gradient(circle at 30% 30%, hsla(${hue},80%,72%,0.55), hsla(${hue},75%,40%,0.85))`,
        border: `1px solid hsla(${hue},75%,60%,0.4)`,
        fontWeight: 700,
        fontSize: size * 0.4,
        color: '#fff',
        flexShrink: 0,
      }}
    >
      {initials || '?'}
    </span>
  );
}

// ─── Page ────────────────────────────────────────────────────

type KeepChoice = 'summary' | 'pause' | 'none';

export default function RoomEnd() {
  const { mediationId } = useParams<{ mediationId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const uid = user?.uid ?? null;

  const [keep, setKeep] = useState<KeepChoice>('pause');
  const [mediation, setRoom] = useState<Room | null>(null);
  const [submitting, setSubmitting] = useState<KeepChoice | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [otherName, setOtherName] = useState('them');
  const [myFirst, setMyFirst] = useState('you');

  // Subscribe (used only to derive the other participant uid for the name fetch)
  useEffect(() => {
    if (!mediationId) return undefined;
    const unsub = subscribeToRoom(
      mediationId,
      (data) => {
        if (data) setRoom(data);
      },
      () => {
        /* non-fatal */
      },
    );
    return () => {
      try {
        unsub();
      } catch {
        /* ignore */
      }
    };
  }, [mediationId]);

  useEffect(() => {
    if (!mediationId) return;
    let cancelled = false;
    (async () => {
      try {
        const { self, counterpart } = await getMediationCounterpart(mediationId);
        if (cancelled) return;
        if (self.firstName) setMyFirst(self.firstName.toLowerCase());
        if (counterpart.firstName) setOtherName(counterpart.firstName.toLowerCase());
      } catch {
        /* leave fallbacks */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [mediationId]);

  const anyLoading = submitting !== null;

  const handleStepOut = async () => {
    if (!mediationId || anyLoading) return;
    setError(null);
    if (keep === 'summary') {
      setSubmitting('summary');
      try {
        await endRoomKeepSummary(mediationId);
        navigate(`/rooms/${mediationId}/summary`, { replace: true });
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : 'something went wrong.');
        setSubmitting(null);
      }
      return;
    }
    if (keep === 'pause') {
      setSubmitting('pause');
      try {
        await pauseRoom(mediationId);
        navigate(-1);
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : 'something went wrong.');
        setSubmitting(null);
      }
      return;
    }
    if (keep === 'none') {
      setSubmitting('none');
      try {
        await deleteRoomForBoth(mediationId);
        navigate('/rooms', { replace: true });
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : 'something went wrong.');
        setSubmitting(null);
      }
    }
  };

  // "what {other} will see" preview text — updates live with the choice
  const previewLine =
    keep === 'summary'
      ? `${myFirst} stepped out of the room. you have a summary to keep.`
      : keep === 'pause'
        ? `${myFirst} stepped out of the room. the room is paused.`
        : `${myFirst} stepped out of the room. nothing remains.`;

  // Use a fixed hue tied to the other side's role (warm if other is inviter,
  // cool otherwise). Inviter = warm magenta hue 325; invitee = cool indigo 258.
  const otherIsInviter = !!mediation && !!uid && mediation.inviterUid !== uid;
  const otherHue = otherIsInviter ? 325 : 258;

  const containerStyle: CSSProperties = {
    position: 'relative',
    // Fill the shell's <main> and let the body scroll, rather than a full-viewport
    // min-height that overflows its slot (same fix as the other room screens).
    height: '100%',
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    background: '#0a0510',
    color: '#fff',
    fontFamily: "'Inter', system-ui, sans-serif",
    overflow: 'hidden',
  };

  return (
    <div style={containerStyle}>
      <SEO title="Stepping out · f8" />
      <DualAurora />

      {/* top bar */}
      <div
        style={{
          position: 'relative',
          zIndex: 1,
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '14px 24px 12px',
        }}
      >
        <button
          type="button"
          onClick={() => navigate(-1)}
          aria-label="back"
          style={{
            appearance: 'none',
            cursor: 'pointer',
            border: 0,
            padding: '6px 8px 6px 0',
            background: 'transparent',
            color: 'rgba(232,220,250,0.6)',
            fontSize: 12,
            fontWeight: 600,
            fontFamily: 'inherit',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
          }}
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path
              d="M14.5 5l-7 7 7 7"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          back
        </button>
        <div
          style={{
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: '0.16em',
            textTransform: 'uppercase',
            color: 'rgba(232,220,250,0.4)',
          }}
        >
          stepping out · a clean exit
        </div>
        <div style={{ width: 40 }} />
      </div>

      {/* body */}
      <main
        style={{
          position: 'relative',
          zIndex: 1,
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          width: '100%',
          padding: '20px 28px 80px',
          maxWidth: 820,
          margin: '0 auto',
        }}
      >
        <div style={{ maxWidth: 720, margin: '4px auto 6px' }}>
          <h1
            style={{
              margin: '0 0 10px',
              fontSize: 26,
              fontWeight: 700,
              color: '#fff',
              letterSpacing: '-0.028em',
              lineHeight: 1.14,
            }}
          >
            stepping out
          </h1>
          <p
            style={{
              margin: 0,
              maxWidth: 580,
              fontSize: 14.5,
              lineHeight: 1.55,
              color: 'rgba(232,220,250,0.6)',
            }}
          >
            a real choice, and none of these is wrong — leave and not come back, leave with the door open, or just close the room for now.
          </p>
        </div>

        <InlineRailCard eyebrow="what would you like to keep?">
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 10,
              marginBottom: 22,
              maxWidth: 620,
            }}
          >
            <ExitOption
              on={keep === 'summary'}
              onClick={() => setKeep('summary')}
              head="close the room, keep a short summary"
              sub="i write one paragraph each of you can keep. the conversation itself is sealed."
              disabled={anyLoading}
            />
            <ExitOption
              on={keep === 'pause'}
              onClick={() => setKeep('pause')}
              head="keep the room, just pause"
              sub="nothing is deleted. either of you can re-open it later."
              disabled={anyLoading}
            />
            <ExitOption
              on={keep === 'none'}
              onClick={() => setKeep('none')}
              head="close the room, keep nothing"
              sub="everything from this room is removed for both of you. solo prep is unaffected."
              disabled={anyLoading}
            />
          </div>

          {/* what {other} will see */}
          <div
            style={{
              padding: '14px 16px',
              borderRadius: 14,
              background: 'rgba(255,255,255,0.03)',
              border: '1px solid rgba(255,255,255,0.07)',
              maxWidth: 620,
            }}
          >
            <div
              style={{
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: '0.16em',
                textTransform: 'uppercase',
                color: 'rgba(232,220,250,0.5)',
                marginBottom: 10,
              }}
            >
              what {otherName} will see
            </div>
            <div
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 11,
                fontSize: 14,
                color: '#fff',
                lineHeight: 1.55,
              }}
            >
              <Avatar name={otherName} hue={otherHue} size={26} />
              <div style={{ paddingTop: 2 }}>
                <i>&ldquo;{previewLine}&rdquo;</i>
              </div>
            </div>
            <div
              style={{
                fontSize: 11.5,
                color: 'rgba(232,220,250,0.45)',
                marginTop: 10,
                fontStyle: 'italic',
              }}
            >
              no reason is shared. no notification at 11pm. i tell {otherName} tomorrow morning.
            </div>
          </div>

          <div
            style={{
              marginTop: 22,
              display: 'flex',
              alignItems: 'center',
              gap: 14,
              flexWrap: 'wrap',
            }}
          >
            <button
              type="button"
              onClick={handleStepOut}
              disabled={anyLoading}
              style={{
                appearance: 'none',
                cursor: anyLoading ? 'progress' : 'pointer',
                padding: '12px 22px',
                borderRadius: 999,
                background: 'rgba(255,255,255,0.08)',
                border: '1px solid rgba(255,255,255,0.14)',
                color: '#fff',
                fontSize: 13.5,
                fontWeight: 600,
                fontFamily: 'inherit',
                opacity: anyLoading ? 0.6 : 1,
              }}
            >
              {submitting === 'summary'
                ? 'sealing…'
                : submitting === 'pause'
                  ? 'pausing…'
                  : submitting === 'none'
                    ? 'closing…'
                    : 'step out'}
            </button>
          </div>

          {error && (
            <div
              role="alert"
              style={{
                marginTop: 14,
                padding: '10px 14px',
                borderRadius: 12,
                background: 'rgba(239,68,68,0.08)',
                border: '1px solid rgba(239,68,68,0.25)',
                fontSize: 13,
                color: '#fca5a5',
                maxWidth: 620,
              }}
            >
              {error}
            </div>
          )}

          {/* PPL-190 — the quiet block exit hatch. Blocking closes any live
              room server-side (indistinguishable from delete-for-both), so
              afterwards we just leave for the list. */}
          {uid && mediation && (
            <RoomBlockHatch
              otherUid={uid === mediation.inviterUid ? mediation.inviteeUid : mediation.inviterUid}
              otherName={otherName}
              onBlocked={() => navigate('/rooms', { replace: true })}
            />
          )}
        </InlineRailCard>
      </main>
    </div>
  );
}
